import { useEffect, useState } from 'react';
import { X, HandWaving, TShirt } from '@phosphor-icons/react';
import { useBodyScrollLock } from '@/lib/useBodyScrollLock';
import { track } from '@/lib/track';

const WelcomePopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  useBodyScrollLock(isOpen);

  useEffect(() => {
    // Check if user has seen the welcome message before
    const hasSeenWelcome = localStorage.getItem('hasSeenWelcome');
    
    if (!hasSeenWelcome) {
      // Show popup after a short delay for better UX
      setTimeout(() => {
        setIsOpen(true);
        // This is every first-time visitor's first interaction with the site,
        // so it's the top of the funnel — worth its own impression event.
        track('welcome_popup_shown');
      }, 1000);
    }
  }, []);

  const handleDismiss = (method = 'close_button') => {
    track('welcome_popup_dismiss', { method });
    setIsOpen(false);
    localStorage.setItem('hasSeenWelcome', 'true');
  };

  const handleBackdropClick = (e) => {
    // Only close if clicking the backdrop, not the content
    if (e.target === e.currentTarget) {
      handleDismiss('backdrop');
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-fadeIn"
      onClick={handleBackdropClick}
      data-testid="welcome-popup-overlay"
    >
      <div 
        className="bg-white w-full max-w-lg rounded-md border border-[#E4E1D9] p-8 relative animate-scaleIn"
        data-testid="welcome-popup-content"
      >
        {/* Close button */}
        <button
          onClick={() => handleDismiss('close_button')}
          className="absolute top-4 right-4 text-[#6B6A68] hover:text-[#1A1A1A] transition-colors"
          data-testid="welcome-popup-close"
        >
          <X size={24} />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F0EFEB] rounded-full">
            <HandWaving size={32} weight="duotone" className="text-[#2D302A]" />
          </div>
        </div>

        {/* Content */}
        <div className="text-center space-y-4 mb-8">
          <h2 
            className="text-3xl md:text-4xl tracking-tighter" 
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Welcome to North of Six Club
          </h2>
          
          <p className="text-base text-[#1A1A1A] leading-relaxed">
            A community built by tall people, for tall people. Discover products that actually fit your height.
          </p>

          <p className="text-sm text-[#6B6A68] leading-relaxed">
            We're <span className="text-[#1A1A1A] font-medium">1,000+ strong</span> and keep growing - just like our height.
          </p>

          {/* Give and Take section */}
          <div className="bg-[#F9F8F6] rounded-md p-6 mt-6 text-left">
            <div className="flex items-start gap-3 mb-3">
              <TShirt size={24} weight="duotone" className="text-[#2D302A] mt-1 flex-shrink-0" />
              <div>
                <h3 
                  className="text-lg tracking-tight mb-2" 
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  It's All About Give & Take
                </h3>
                <p className="text-sm text-[#6B6A68] leading-relaxed">
                  Love what you found here? Share your own perfect fits too! When you contribute, 
                  you help fellow tall folks find products that actually work. The more we share, 
                  the stronger our community grows.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Got it button */}
        <button
          onClick={() => handleDismiss('got_it')}
          className="w-full bg-[#2D302A] text-white px-6 py-4 rounded-md hover:bg-[#1A1C18] transition-colors duration-300"
          style={{ fontFamily: 'IBM Plex Mono, monospace' }}
          data-testid="welcome-popup-dismiss"
        >
          <span className="text-xs uppercase tracking-[0.2em]">Got It</span>
        </button>

        <p className="text-xs text-center text-[#6B6A68] mt-4">
          You can start exploring right away
        </p>
      </div>
    </div>
  );
};

export default WelcomePopup;
